import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { createCheckoutSession } from '../services/api';
import { FaCheck } from 'react-icons/fa';
import useDocumentTitle from '../UseDocumentTitle';
import '../Styles/Pricing.css';
import { handleStripePortalRedirect } from '../services/api';

const Pricing = () => {
    useDocumentTitle('StudyBuddy - Pricing');
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const subscription = user?.subscription;

    const [currency, setCurrency] = useState('usd');
    const [billingCycle, setBillingCycle] = useState('monthly');

    const pricingTiers = [
        { 
            name: 'Free', 
            price: { usd: 'Free', cad: 'Free' },
            features: [
                'Access to core AI capabilities',
                'Daily interaction limits of 3 chats',
            ]
        },
        { 
            name: 'Premium', 
            price: { 
                usd: { monthly: '$5.59', annual: '$49.99' },
                cad: { monthly: '$6.99', annual: '$59.99' }
            },
            freeTrial: '7 days',
            features: [
                'Access to core AI capabilities',
                'Increased interaction limits',
                
            ]
        },
        { 
            name: 'Pro', 
            price: { 
                usd: { monthly: '$12.99', annual: '$119.99' },
                cad: { monthly: '$15.99', annual: '$139.99' }
            },
            freeTrial: '7 days',
            features: [
                'Everything in Premium, plus:',
                'Unlimited AI interactions',
                'Priority support'
            ]
        },
        { 
            name: 'Ultimate', 
            price: { 
                usd: { monthly: '$18.99', annual: '$159.99' },
                cad: { monthly: '$21.99', annual: '$189.99' }
            },
            freeTrial: '7 days', 
            features: [
                'Everything in Pro, plus:',
                'Priority response times',
                'Access to beta features',

            ]
        }
    ];

    useEffect(() => {
        const userLanguage = navigator.language || navigator.userLanguage;
        if (userLanguage.toLowerCase().includes('ca')) {
            setCurrency('cad');
        } else {
            setCurrency('usd');
        }
    }, []);

    const handleSubscription = async (tier, interval = billingCycle, currencyCode = currency) => {
        if (!isAuthenticated) {
            navigate('/signup', { state: { selectedTier: tier, interval, currency: currencyCode } });
            return;
        }

        try {
            if(subscription && subscription.tier !== "free"){
                handleStripePortalRedirect();
                return;
            }
            const response = await createCheckoutSession(tier, interval, currencyCode);
            if (response.url) {
                window.location.href = response.url;
            } else {
                console.error('Unexpected response format:', response);
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
        }
    };

    const renderButton = (tier) => {
        if (!user) {
            return (
                <button 
                    onClick={() => handleSubscription(tier.name.toLowerCase())} 
                    className="signup-button"
                >
                    {tier.name.toLowerCase() === 'free' ? 'Get Started' : `Choose ${tier.name}`}
                </button>
            );
        }

        if (subscription && subscription.tier !== 'free') {
            if (subscription.tier.toLowerCase() === tier.name.toLowerCase()) {
                return (
                    <button 
                        onClick={handleStripePortalRedirect} 
                        className="signup-button"
                    >
                        Manage Subscription
                    </button>
                );
            } else {
                return (
                    <button 
                        className="signup-button" 
                        style={{visibility: 'hidden'}} 
                        disabled
                    >
                        Invisible
                    </button>
                );
            }
        }

        // For free users
        return (
            <button 
                onClick={() => handleSubscription(tier.name.toLowerCase())} 
                className="signup-button"
            >
                {tier.name.toLowerCase() === 'free' ? 'Current Plan' : `Choose ${tier.name}`}
            </button>
        );
    };

    return (
        <div className="pricing-page">
            <header className="pricing-header">
                <h1>Choose Your Perfect Plan</h1>
                <p>Unlock your full potential with our flexible pricing options</p>
            </header>

            <section className="pricing-controls">
                <div className="toggle-container">
                    <span className={currency === 'usd' ? 'active' : ''}>USD</span>
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={currency === 'cad'}
                            onChange={() => setCurrency(currency === 'usd' ? 'cad' : 'usd')}
                        />
                        <span className="slider round"></span>
                    </label>
                    <span className={currency === 'cad' ? 'active' : ''}>CAD</span>
                </div>
                <div className="toggle-container">
                    <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={billingCycle === 'annual'}
                            onChange={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                        />
                        <span className="slider round"></span>
                    </label>
                    <span className={billingCycle === 'annual' ? 'active' : ''}>Annual</span>
                </div>
            </section>

            <section className="pricing-grid">
                {pricingTiers.map((tier) => (
                    <div key={tier.name} className={`pricing-item ${tier.name === 'Pro' ? 'featured' : ''}`}>
                        <h3>{tier.name}</h3>
                        {tier.name !== 'Free' ? (
                            <>
                                <p className="price">
                                    {tier.price[currency][billingCycle]}
                                    <span className="billing-cycle">
                                        {billingCycle === 'monthly' ? '/month' : '/year'}
                                    </span>
                                </p>
                                {billingCycle === 'annual' && (
                                    <p className="annual-savings">
                                        Save {(() => {
                                            const monthlyPrice = parseFloat(tier.price[currency].monthly.replace(/[^0-9.]/g, ''));
                                            const annualPrice = parseFloat(tier.price[currency].annual.replace(/[^0-9.]/g, ''));
                                            return ((monthlyPrice * 12 - annualPrice) / (monthlyPrice * 12) * 100).toFixed(0);
                                        })()}%
                                    </p>
                                )}
                            </>
                        ) : (
                            <p className="price">{tier.price[currency]}</p>
                        )}
                        {tier.freeTrial && <p className="free-trial">{tier.freeTrial} free trial</p>}
                        <ul className="feature-list">
                            {tier.features.map((feature, index) => (
                                <li key={index}><FaCheck className="feature-check" /> {feature}</li>
                            ))}
                        </ul>
                        {renderButton(tier)}
                    </div>
                ))}
            </section>

            <section className="faq">
                <h2>Frequently Asked Questions</h2>
                <div className="faq-grid">
                    <div className="faq-item">
                        <h3>What payment methods do you accept?</h3>
                        <p>We accept all major credit cards, including Visa, MasterCard, American Express, and Discover.</p>
                    </div>
                    <div className="faq-item">
                        <h3>Can I cancel my subscription at any time?</h3>
                        <p>Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period.</p>
                    </div>
                    <div className="faq-item">
                        <h3>Is there a refund policy?</h3>
                        <p>We offer a 30-day money-back guarantee for all paid plans, reach out to support, it will be dealt with on a case by case basis.</p>
                    </div>
                    <div className="faq-item">
                        <h3>Can I upgrade or downgrade my plan?</h3>
                        <p>Yes, you can change your plan at any time. The changes will be prorated for the remainder of your billing cycle.</p>
                    </div>
                </div>
            </section>

            <section className="cta">
                <h2>Ready to Start Your Learning Journey?</h2>
                <p>Choose the plan that's right for you and begin exploring the power of AI-assisted learning today.</p>
                <Link to="/signup" className="cta-button">Get Started Now</Link>
            </section>
        </div>
    );
};

export default Pricing;